import ApplicationController from './application_controller'

// Constants for scroll behavior
const SCROLL_RATIO = 0.9 // Scroll 90% of viewport width per arrow click
const DRAG_THRESHOLD = 2 // Minimum pixel movement to register as drag
const SUB_PIXEL_TOLERANCE = 1 // Tolerance for sub-pixel precision issues
const CLICK_PREVENTION_DELAY = 10 // Delay to prevent click after drag (ms)
const LEFT_MOUSE_BUTTON = 0 // Mouse button code for left click

// Connects to data-controller="swimlane"
export default class SwimlaneController extends ApplicationController {
  static targets = ["content", "prevArrow", "nextArrow"]

  connect() {
    this.updateArrowStates()
    this.isDragging = false
    this.dragStarted = false

    // Bind functions once to avoid creating new references
    this.boundDrag = this.drag.bind(this)
    this.boundEndDrag = this.endDrag.bind(this)
  }

  // Handle next arrow click
  next() {
    if (this.nextArrowTarget.classList.contains('disabled')) return
    this.performScroll(1) // Scroll forward
  }

  // Handle previous arrow click
  prev() {
    if (this.prevArrowTarget.classList.contains('disabled')) return
    this.performScroll(-1) // Scroll backward
  }

  // Common scroll logic for both directions
  performScroll(direction) {
    const scrollAmount = this.contentTarget.clientWidth * SCROLL_RATIO
    const currentScroll = this.contentTarget.scrollLeft
    const maxScroll = this.contentTarget.scrollWidth - this.contentTarget.clientWidth

    // Calculate new scroll position based on direction
    const newScroll = direction > 0
      ? Math.min(currentScroll + scrollAmount, maxScroll) // Forward: don't exceed max
      : Math.max(currentScroll - scrollAmount, 0) // Backward: don't go below 0

    this.contentTarget.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    })
  }

  // Update disabled states of arrows based on scroll position
  updateArrowStates() {
    const scrollLeft = this.contentTarget.scrollLeft
    const maxScroll = this.contentTarget.scrollWidth - this.contentTarget.clientWidth

    // Update previous arrow
    if (scrollLeft <= 0) {
      this.prevArrowTarget.classList.add('disabled')
    } else {
      this.prevArrowTarget.classList.remove('disabled')
    }

    // Update next arrow
    if (scrollLeft >= maxScroll - SUB_PIXEL_TOLERANCE) {
      this.nextArrowTarget.classList.add('disabled')
    } else {
      this.nextArrowTarget.classList.remove('disabled')
    }
  }

  // Handle mouse drag functionality
  startDrag(event) {
    // Only the left mouse button initiates drag scrolling
    if (event.button !== LEFT_MOUSE_BUTTON) return

    event.preventDefault()
    event.stopPropagation()

    this.isDragging = true
    this.dragStarted = false
    this.startX = event.clientX
    this.startScrollLeft = this.contentTarget.scrollLeft

    // Add event listeners for drag
    document.addEventListener('mousemove', this.boundDrag)
    document.addEventListener('mouseup', this.boundEndDrag)

    // Prevent text selection during drag
    document.body.classList.add('swimlane-dragging')
  }

  drag(event) {
    if (!this.isDragging) return

    event.preventDefault()

    const deltaX = event.clientX - this.startX
    const newScrollLeft = this.startScrollLeft - deltaX

    // Mark that we've started dragging if there's any movement
    if (Math.abs(deltaX) > DRAG_THRESHOLD) {
      this.dragStarted = true
    }

    // Constrain scroll to valid range
    const maxScroll = this.contentTarget.scrollWidth - this.contentTarget.clientWidth
    this.contentTarget.scrollLeft = Math.max(0, Math.min(newScrollLeft, maxScroll))

    // Update arrow states during drag
    this.updateArrowStates()
  }

  endDrag() {
    this.isDragging = false

    // Remove event listeners
    document.removeEventListener('mousemove', this.boundDrag)
    document.removeEventListener('mouseup', this.boundEndDrag)

    // Restore cursor and text selection
    document.body.classList.remove('swimlane-dragging')

    // Keep dragStarted flag set until after events queued so far (including any clicks) go through
    if (this.dragStarted) {
      setTimeout(() => {
        this.dragStarted = false
      }, CLICK_PREVENTION_DELAY)
    }
  }

  // Prevent click events automatically generated by the browser after drag mouseup
  handleContentClick(event) {
    if (this.dragStarted) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  // Handle scroll events to update arrow states
  onScroll() {
    this.updateArrowStates()
  }

  disconnect() {
    // Clean up any ongoing drag operations
    if (this.isDragging) {
      this.endDrag()
    }
  }
}
